/**
 * Analytics Service
 *
 * Provider-agnostic analytics integration service.
 * Supports multiple analytics providers (Mixpanel, Amplitude, Google Analytics, etc.)
 * with a unified interface.
 */

// ═══════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Event properties that can be tracked
 */
export type EventProperties = Record<string, unknown>;

/**
 * User traits for identification
 */
export type UserTraits = Record<string, unknown>;

/**
 * Error context for error tracking
 */
export interface ErrorContext {
  /** Component or module where the error occurred */
  component?: string;
  /** User action that triggered the error */
  action?: string;
  /** Additional context data */
  [key: string]: unknown;
}

/**
 * Page view properties
 */
export interface PageViewProperties {
  /** Page title */
  title?: string;
  /** Page path/URL */
  path?: string;
  /** Referrer URL */
  referrer?: string;
  /** Additional properties */
  [key: string]: unknown;
}

/**
 * Analytics provider interface
 * Implement this interface to create custom analytics providers
 */
export interface AnalyticsProvider {
  /** Provider name for identification */
  readonly name: string;

  /**
   * Initialize the provider
   * Called when the provider is registered
   */
  initialize?(): Promise<void> | void;

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: EventProperties): void;

  /**
   * Track a page view
   */
  trackPageView(pageName: string, properties?: PageViewProperties): void;

  /**
   * Track an error
   */
  trackError(error: Error, context?: ErrorContext): void;

  /**
   * Identify a user
   */
  identify(userId: string, traits?: UserTraits): void;

  /**
   * Reset analytics state (for logout)
   */
  reset(): void;

  /**
   * Set super properties (properties included with every event)
   */
  setSuperProperties?(properties: EventProperties): void;

  /**
   * Cleanup when provider is removed
   */
  destroy?(): void;
}

/**
 * Analytics service configuration
 */
export interface AnalyticsConfig {
  /** Whether analytics is enabled */
  enabled: boolean;
  /** Enable debug mode (console logging) */
  debug: boolean;
  /** App identifier */
  appName: string;
  /** App version */
  appVersion?: string;
  /** Environment (development, staging, production) */
  environment: 'development' | 'staging' | 'production';
  /** Default properties to include with every event */
  defaultProperties?: EventProperties;
}

/**
 * Helper to safely get environment mode
 */
const getEnvMode = (): string => {
  // Node.js / React Native / Metro environment
  if (typeof process !== 'undefined' && process.env) {
    return process.env['NODE_ENV'] || 'development';
  }
  return 'development';
};

/**
 * Default analytics configuration
 */
const DEFAULT_CONFIG: AnalyticsConfig = {
  enabled: true,
  debug: getEnvMode() === 'development',
  appName: 'tonnie',
  environment: (getEnvMode() as AnalyticsConfig['environment']) || 'development',
};

// ═══════════════════════════════════════════════════════════════════════
// CONSOLE PROVIDER (Development/Debug)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Console analytics provider
 * Logs all analytics events to the console for development/debugging
 */
export class ConsoleAnalyticsProvider implements AnalyticsProvider {
  readonly name = 'console';
  private superProperties: EventProperties = {};

  private log(type: string, ...args: unknown[]): void {
    const timestamp = new Date().toISOString();
    console.log(`[Analytics][${timestamp}][${type}]`, ...args);
  }

  trackEvent(name: string, properties?: EventProperties): void {
    this.log('Event', name, { ...this.superProperties, ...properties });
  }

  trackPageView(pageName: string, properties?: PageViewProperties): void {
    this.log('PageView', pageName, { ...this.superProperties, ...properties });
  }

  trackError(error: Error, context?: ErrorContext): void {
    this.log('Error', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...context,
    });
  }

  identify(userId: string, traits?: UserTraits): void {
    this.log('Identify', userId, traits);
  }

  reset(): void {
    this.superProperties = {};
    this.log('Reset', 'Analytics state cleared');
  }

  setSuperProperties(properties: EventProperties): void {
    this.superProperties = { ...this.superProperties, ...properties };
    this.log('SuperProperties', this.superProperties);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// NOOP PROVIDER (Production fallback)
// ═══════════════════════════════════════════════════════════════════════

/**
 * No-operation analytics provider
 * Used when analytics is disabled or no provider is configured
 */
export class NoopAnalyticsProvider implements AnalyticsProvider {
  readonly name = 'noop';

  trackEvent(): void {
    // No-op
  }

  trackPageView(): void {
    // No-op
  }

  trackError(): void {
    // No-op
  }

  identify(): void {
    // No-op
  }

  reset(): void {
    // No-op
  }
}

// ═══════════════════════════════════════════════════════════════════════
// ANALYTICS SERVICE
// ═══════════════════════════════════════════════════════════════════════

/**
 * Analytics Service
 *
 * Central analytics service that delegates to registered providers.
 * Supports multiple providers for different analytics platforms.
 */
export class AnalyticsService {
  private providers: Map<string, AnalyticsProvider> = new Map();
  private config: AnalyticsConfig;
  private superProperties: EventProperties = {};
  private userId: string | null = null;
  private userTraits: UserTraits = {};

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Add console provider in debug mode
    if (this.config.debug) {
      this.registerProvider(new ConsoleAnalyticsProvider());
    }
  }

  /**
   * Register an analytics provider
   */
  async registerProvider(provider: AnalyticsProvider): Promise<void> {
    if (this.providers.has(provider.name)) {
      console.warn(`Analytics provider "${provider.name}" is already registered`);
      return;
    }

    if (provider.initialize) {
      await provider.initialize();
    }

    this.providers.set(provider.name, provider);

    // Apply existing super properties to new provider
    if (provider.setSuperProperties && Object.keys(this.superProperties).length > 0) {
      provider.setSuperProperties(this.superProperties);
    }

    // Re-identify user if already identified
    if (this.userId) {
      provider.identify(this.userId, this.userTraits);
    }
  }

  /**
   * Remove an analytics provider
   */
  removeProvider(name: string): void {
    const provider = this.providers.get(name);
    if (provider?.destroy) {
      provider.destroy();
    }
    this.providers.delete(name);
  }

  /**
   * Get a registered provider by name
   */
  getProvider<T extends AnalyticsProvider>(name: string): T | undefined {
    return this.providers.get(name) as T | undefined;
  }

  /**
   * Check if analytics is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled && this.providers.size > 0;
  }

  /**
   * Set super properties (included with every event)
   */
  setSuperProperties(properties: EventProperties): void {
    this.superProperties = { ...this.superProperties, ...properties };
    this.providers.forEach((provider) => {
      provider.setSuperProperties?.(this.superProperties);
    });
  }

  /**
   * Clear super properties
   */
  clearSuperProperties(): void {
    this.superProperties = {};
  }

  /**
   * Get merged properties with defaults and super properties
   */
  private getMergedProperties(properties?: EventProperties): EventProperties {
    return {
      ...this.config.defaultProperties,
      ...this.superProperties,
      appName: this.config.appName,
      appVersion: this.config.appVersion,
      environment: this.config.environment,
      timestamp: new Date().toISOString(),
      ...properties,
    };
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: EventProperties): void {
    if (!this.config.enabled) return;

    const mergedProperties = this.getMergedProperties(properties);

    this.providers.forEach((provider) => {
      try {
        provider.trackEvent(name, mergedProperties);
      } catch (error) {
        console.error(`Analytics error in provider "${provider.name}":`, error);
      }
    });
  }

  /**
   * Track a page view
   */
  trackPageView(pageName: string, properties?: PageViewProperties): void {
    if (!this.config.enabled) return;

    const mergedProperties = this.getMergedProperties(properties) as PageViewProperties;

    this.providers.forEach((provider) => {
      try {
        provider.trackPageView(pageName, mergedProperties);
      } catch (error) {
        console.error(`Analytics error in provider "${provider.name}":`, error);
      }
    });
  }

  /**
   * Track an error
   */
  trackError(error: Error, context?: ErrorContext): void {
    if (!this.config.enabled) return;

    const enrichedContext: ErrorContext = {
      ...context,
      ...this.config.defaultProperties,
      appName: this.config.appName,
      appVersion: this.config.appVersion,
      environment: this.config.environment,
      timestamp: new Date().toISOString(),
    };

    this.providers.forEach((provider) => {
      try {
        provider.trackError(error, enrichedContext);
      } catch (err) {
        console.error(`Analytics error in provider "${provider.name}":`, err);
      }
    });
  }

  /**
   * Identify a user
   */
  identify(userId: string, traits?: UserTraits): void {
    if (!this.config.enabled) return;

    this.userId = userId;
    this.userTraits = traits || {};

    this.providers.forEach((provider) => {
      try {
        provider.identify(userId, traits);
      } catch (error) {
        console.error(`Analytics error in provider "${provider.name}":`, error);
      }
    });
  }

  /**
   * Reset analytics state (for logout)
   */
  reset(): void {
    this.userId = null;
    this.userTraits = {};
    this.superProperties = {};

    this.providers.forEach((provider) => {
      try {
        provider.reset();
      } catch (error) {
        console.error(`Analytics error in provider "${provider.name}":`, error);
      }
    });
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get current config
   */
  getConfig(): AnalyticsConfig {
    return { ...this.config };
  }

  /**
   * Update config
   */
  updateConfig(config: Partial<AnalyticsConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Destroy all providers and cleanup
   */
  destroy(): void {
    this.providers.forEach((provider) => {
      provider.destroy?.();
    });
    this.providers.clear();
    this.reset();
  }
}

// ═══════════════════════════════════════════════════════════════════════
// FACTORY FUNCTION
// ═══════════════════════════════════════════════════════════════════════

/**
 * Create an analytics service instance
 */
export function createAnalyticsService(config?: Partial<AnalyticsConfig>): AnalyticsService {
  return new AnalyticsService(config);
}

// ═══════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE (optional)
// ═══════════════════════════════════════════════════════════════════════

let defaultInstance: AnalyticsService | null = null;

/**
 * Get or create the default analytics service instance
 */
export function getAnalyticsService(config?: Partial<AnalyticsConfig>): AnalyticsService {
  if (!defaultInstance) {
    defaultInstance = createAnalyticsService(config);
  }
  return defaultInstance;
}

/**
 * Reset the default analytics service instance
 */
export function resetAnalyticsService(): void {
  if (defaultInstance) {
    defaultInstance.destroy();
    defaultInstance = null;
  }
}
